import type {
  MachineAttachmentDto,
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import {
  ARCHIVED_ITEMS,
  displayOldPrice,
  formatPriceAndCurrency,
  Options,
  ServicesTypes,
  TRANSPORT_OPTION_TYPES
} from '@common/utils'
import {
  type IconColor,
  type IconVariant,
  MolsonAccordion,
  MolsonBox,
  MolsonTypography
} from '@components/reusable'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ArchivedNotification } from '../archived-notification/ArchivedNotification'
import type { HandleOptionChangeFunctionProps } from '../types'
import { PriceDifferenceDisplay } from './components/PriceDifferenceDisplay'
import { TransportAutocomplete } from './components/TransportAutocomplete'
import { TransportRadioGroup } from './components/TransportRadioGroup'

type TransportProps = {
  changeTransportType: (prop: string) => Promise<void>
  expandedServicesSections?: Set<string>
  handleOptionChange: HandleOptionChangeFunctionProps
  isWithinMachine?: boolean
  machine: MachineAttachmentDto | MachineDto
  machineQuotation: MachineAttachmentItemDto & MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  sectionError?: boolean
  selectedTransportType: string
  setSelectedTransportType: (val: string) => void
  translationsPrefix: string
  toggleExpandServicesSection?: (key: string) => void
}

export const Transport = ({
  changeTransportType,
  expandedServicesSections,
  handleOptionChange,
  isWithinMachine,
  machine,
  machineQuotation,
  quotationItemOldPrices,
  sectionError,
  selectedTransportType,
  setSelectedTransportType,
  toggleExpandServicesSection,
  translationsPrefix
}: TransportProps) => {
  const { t } = useTranslation('', { keyPrefix: translationsPrefix })

  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParam,
    specId: specIdParam
  } = useParams()

  const quoteId = quoteIdParam ?? specIdParam

  const itemKey = `${ARCHIVED_ITEMS.TRANSPORT}_${machineQuotation?.transportOption?.id}`
  const [selectedRadioOption, setSelectedRadioOption] = useState(
    selectedTransportType
  )
  const [selectedTransportOption, setSelectedTransportOption] = useState(
    machineQuotation.transportOption ?? ''
  )
  const [expanded, setExpanded] = useState(
    expandedServicesSections?.has(ServicesTypes.TRANSPORT) || sectionError
  )
  const selectedTransportDnp = machineQuotation?.transportOption?.dnp

  const selectedTransportOldDnp =
    quotationItemOldPrices.transportOption?.dnp ?? undefined

  const displayPriceDifference = displayOldPrice(
    selectedTransportDnp,
    selectedTransportOldDnp
  )

  const getTransportOptions = (selectedTransportType: string) => {
    const { transportOptions } = machine
    if (!transportOptions) {
      return []
    }
    return transportOptions?.filter(
      option =>
        option.optionType === selectedTransportType && !option.isArchived
    )
  }

  const isTransportItemArchived = machineQuotation.transportOption?.isArchived

  const handleChangeTransportRadioOption = (value: string) => {
    setSelectedRadioOption(value)
    setSelectedTransportType(value)
    changeTransportType(value)
    setSelectedTransportOption('')
  }

  const removeTransportOption = () => {
    if (machineQuotation.transportOption) {
      if (equipmentId && attachmentId) {
        handleOptionChange({
          machineId: equipmentId,
          option: Options.REMOVE_TRANSPORT_OPTION_WITHIN_MACHINE,
          optionId: Number(machineQuotation.transportOption?.id),
          quotationId: quoteId as string
        })
      } else {
        handleOptionChange({
          option: Options.REMOVE_TRANSPORT_OPTION,
          optionId: Number(machineQuotation.transportOption?.id),
          quotationId: quoteId as string
        })
      }
    }
  }

  const addTransportOption = (optionId: number) => {
    if (equipmentId && attachmentId) {
      handleOptionChange({
        machineId: equipmentId,
        option: Options.ADD_TRANSPORT_OPTION_WITHIN_MACHINE,
        optionId,
        quotationId: quoteId as string
      })
    } else {
      handleOptionChange({
        option: Options.ADD_TRANSPORT_OPTION,
        optionId,
        quotationId: quoteId as string
      })
    }
  }

  const getSummaryIcon = () => {
    if (isTransportItemArchived) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (displayPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const transportVariantHasAllOptionsArchived = (transportVariant: string) => {
    const { transportOptions } = machine
    const transportVariantList = transportOptions?.filter(
      transport => transport.optionType === transportVariant
    )
    return (
      transportVariantList &&
      transportVariantList.length > 0 &&
      transportVariantList.every(element => element.isArchived)
    )
  }

  const showArchivedNotification = () => {
    const selectedTransportVariantIsArchived =
      transportVariantHasAllOptionsArchived(selectedRadioOption)
    return isTransportItemArchived || selectedTransportVariantIsArchived
  }

  const summaryIcon = getSummaryIcon()

  useEffect(() => {
    setSelectedTransportOption(machineQuotation.transportOption ?? '')
  }, [machineQuotation.transportOption])

  useEffect(() => {
    if (specIdParam) {
      if (machineQuotation.isTransportOptionFitted) {
        setSelectedTransportType(TRANSPORT_OPTION_TYPES.FITTED)
      } else {
        setSelectedTransportType(
          machineQuotation?.transportOption?.optionType ??
            TRANSPORT_OPTION_TYPES.DELIVERY
        )
      }
    }
  }, [
    specIdParam,
    machineQuotation.isTransportOptionFitted,
    machineQuotation?.transportOption?.optionType,
    setSelectedTransportType
  ])

  useEffect(() => {
    if (sectionError) {
      setExpanded(true)
    }
  }, [sectionError])

  return machine ? (
    <MolsonAccordion
      detailsSx={{ paddingTop: 2.5 }}
      onChange={(_, toggle) => {
        setExpanded(toggle)
        toggleExpandServicesSection?.(ServicesTypes.TRANSPORT)
      }}
      expanded={
        expandedServicesSections?.has(ServicesTypes.TRANSPORT) || expanded
      }
      summaryIcon={summaryIcon?.icon as IconVariant}
      summaryIconColor={summaryIcon?.color as IconColor}
      withTooltip={{ description: t('deliveryToAsite'), title: t('transport') }}
      primaryText={t('transport')}
    >
      <TransportRadioGroup
        selectedTransportType={selectedTransportType}
        isWithinMachine={isWithinMachine}
        onChange={value => {
          handleChangeTransportRadioOption(value)
        }}
        translations={{
          fitted: t('fitted'),
          delivery: t('delivery'),
          collection: t('customerCollect')
        }}
      />

      {selectedTransportType !== TRANSPORT_OPTION_TYPES.FITTED && (
        <MolsonBox sx={{ display: 'flex', mt: 5 }}>
          <TransportAutocomplete
            options={getTransportOptions(selectedTransportType)}
            selectedOption={selectedTransportOption}
            onChange={optionId => {
              optionId ? addTransportOption(optionId) : removeTransportOption()
            }}
            placeholder={t('selectTransport')}
            sectionError={sectionError}
            isArchived={showArchivedNotification()}
            displayPriceDifference={displayPriceDifference}
          />
          {displayPriceDifference !== undefined &&
            machineQuotation?.transportOption?.id &&
            !sectionError && (
              <PriceDifferenceDisplay
                oldPrice={selectedTransportOldDnp ?? 0}
                newPrice={selectedTransportDnp ?? 0}
                formatPriceAndCurrency={formatPriceAndCurrency}
              />
            )}
        </MolsonBox>
      )}

      {showArchivedNotification() && (
        <ArchivedNotification id={itemKey} text={t('serviceUnavailable')} />
      )}

      {sectionError && (
        <MolsonTypography variant="error">
          {t('pleaseSelectTransport')}
        </MolsonTypography>
      )}
    </MolsonAccordion>
  ) : null
}
